/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */
import React, { useState } from "react";
import FormFader from "../Common/Fader/FormFader";
import wizardStyle from "../wizardStyle";
import FormSubmit from "../Common/FormSubmit/FormSubmit";
import { useForm } from "../../../provider/Form/FormProvider";
import FormSubmitProps from "../../../types/FormSubmitProps";

import { Box, Alert, Paper } from "@mui/material";
import Grid from "@mui/material/Grid";
import ValidatorTextField from "../../FormFields/ValidatorTextField";
import { AbstractCase } from "../../../types/Entities/AbstractCase";
import Typography from "@mui/material/Typography";
import FormLegend from "../Common/FormLegend/FormLegend";
import { UpdateResourceParams } from "../../../services/reactQuery/reactQueryService";
import { UseMutationResult } from "@tanstack/react-query";
import { translate } from "../../../services/translations/translations";
import { requiredValidator } from "../../../services/validationRules";

type RefinementQuestionsProps = {
  submitProps: FormSubmitProps;
  product: AbstractCase;
  updateMutation: UseMutationResult<any, unknown, UpdateResourceParams>;
};

export default function RefinementQuestions({ submitProps, product, updateMutation }: RefinementQuestionsProps) {
  const { handleSubmit, values } = useForm();
  const [isDisabled, setIsDisabled] = useState(false);

  const handleBackClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    setIsDisabled(true);
    updateMutation.mutateAsync({
      uri: "/pending_cases",
      id: product.id,
      data: { aiRefinementQuestions: null, refinementAnswers: null },
    });
  };

  return (
    <Paper sx={wizardStyle.paper} elevation={4}>
      <FormFader>
        <Box component={"form"} sx={{ ...wizardStyle.applicationForm, ...wizardStyle.wizardForm }} onSubmit={handleSubmit}>
          <Typography variant={"h4"} sx={[wizardStyle.headline, { display: { xs: "block", sm: "none" } }]}>
            Ergänzende Informationen
          </Typography>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline",
            }}
          >
            <Grid size={12}>
              <FormLegend>
                <strong>Ergänzende Informationen</strong>
              </FormLegend>
            </Grid>
            <Grid sx={wizardStyle.gridTopSpaced} size={12}>
              <Alert
                severity="info"
                sx={{
                  mt: "1rem",
                  mb: "2rem",
                  color: (theme) => theme.palette.info.dark,
                }}
              >
                <Typography variant="h6">Bitte lassen Sie uns noch folgende Informationen zukommen:</Typography>
                <Typography dangerouslySetInnerHTML={{ __html: product.aiRefinementQuestions }} />
              </Alert>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name="refinementAnswers"
                label={translate(`pendingCase.refinementAnswers.label`, product)}
                InputLabelProps={{
                  shrink: true,
                }}
                inputProps={{
                  maxLength: 5000,
                }}
                multiline
                rows={10}
                validators={[requiredValidator("Bitte ergänzen Sie die fehlenden Informationen.")]}
                helperText={
                  "[" +
                  (values.refinementAnswers?.length ?? 0) +
                  "/5000]" +
                  ((values.refinementAnswers?.length ?? 0) < 50 ? " Bitte geben Sie mindestens 50 Buchstaben ein" : "")
                }
              />
            </Grid>
          </Grid>
          <FormSubmit {...submitProps} disabled={isDisabled} onBackClick={handleBackClick} />
        </Box>
      </FormFader>
    </Paper>
  );
}
